import { crud, functional } from '@think-internet/zeus-frontend-package'
import Routes from '../../../redux/routes'
import { Answer } from '../../../types/Survey/Answer/Answer'
import { useCallback, useEffect, useState } from 'react'
import { Survey, SurveyType } from '../../../types/Survey/Survey'
import { Element } from '../../../types/Survey/Answer/Element'
import transformElementsToAnswers from './transformElementsToAnswers'
import useToast from '../useToast'
import props from '../../../redux/props'
import { useSelector } from 'react-redux'
import useLoadPublicSurvey from '../useLoadPublicSurvey'
import useGetUserIdentifier from '../useGetUserIdentifier'
import { AccountLink } from '@think-internet/zeus-frontend-package/auth/types'
import moment from 'moment'

export type UpdateElement = (uuid: string) => (answer: Element) => void
export type UpdateDayNumber = (uuid: number) => void
export type UpdateOrganizationUnitUUID = (uuid: string) => void
export type Submit = () => Promise<boolean>
export enum PrefilledType {
  LEADER = 'LEADER',
  MANAGER = 'MANAGER',
  TEAM = 'TEAM',
  NONE = 'NONE',
}
export enum Status {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type Response = {
  survey?: Survey
  surveyAnswer?: Answer
  updateElement?: UpdateElement
  updateOrganizationUnitUUID?: UpdateOrganizationUnitUUID
  updateDayNumber?: UpdateDayNumber
  submit?: Submit
  prefilledType: PrefilledType
  status: Status
  timeLimitReached: boolean
}

const useSurveyAnswer = (surveyUUID: string, previewMode: boolean = false): Response => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const toast = useToast()
  const saveAnswerSet = functional.use(Routes.SAVE_ANSWER_SET)
  const surveyAnswerFeature = crud.use(props.SURVEY_ANSWER)
  const userIdentifier = useGetUserIdentifier()
  const survey = useLoadPublicSurvey(surveyUUID, userIdentifier)
  const [surveyAnswer, setSurveyAnswer] = useState<Answer>(null)
  const [status, setStatus] = useState<Status>(Status.PENDING)
  const [prefilledType, setPrefilledType] = useState<PrefilledType>(PrefilledType.NONE)
  const accountLink: AccountLink = useSelector((s) => s[props.ACCOUNT_LINK])

  const checkForExistingTeamMemberAnswer = useCallback(
    async (survey: Survey) => {
      if (previewMode === true || !userIdentifier || !!accountLink) return false
      const answer = await surveyAnswerFeature.get({ surveyUUID: survey.uuid, userIdentifier })
      return !!answer ? answer : false
    },
    [surveyAnswerFeature, previewMode, userIdentifier, accountLink],
  )

  const checkForExistingLeaderAnswer = useCallback(
    async (survey: Survey) => {
      if (previewMode === true) return false
      const answer = await surveyAnswerFeature.get({ surveyUUID: survey.uuid })
      return !!answer ? answer : false
    },
    [surveyAnswerFeature, previewMode],
  )

  const checkForExistingManagerAnswer = useCallback(
    async (survey: Survey, organizationUnitUUID: String) => {
      if (previewMode === true) return false
      const answer = await surveyAnswerFeature.get({ surveyUUID: survey.uuid, organizationUnitUUID })
      return !!answer ? answer : false
    },
    [surveyAnswerFeature, previewMode],
  )

  const surveyTimeLimitReached = useCallback(() => {
    if (!survey?.context) return false
    if (survey.context.deadline) {
      const now = new Date()
      return now > moment.utc(survey.context.deadline).local(true).toDate()
    }
    return false
  }, [survey])

  useEffect(() => {
    const initAnswer = async () => {
      let elements = transformElementsToAnswers(survey.elements)

      // check for existing answer of leader
      if (survey.surveyType === SurveyType.LEADER) {
        const answers = await checkForExistingLeaderAnswer(survey)
        if (!!answers) {
          elements = answers.elements
          setPrefilledType(PrefilledType.LEADER)
        }
        // check fo exsiting answer of team member
      } else if (survey.surveyType === SurveyType.TEAM) {
        const answer = await checkForExistingTeamMemberAnswer(survey)
        if (!!answer) {
          elements = answer.elements
          setPrefilledType(PrefilledType.TEAM)
        }
      }

      // initialize answer
      setSurveyAnswer({
        surveyUUID: survey.uuid,
        userIdentifier: userIdentifier,
        elements,
      })
    }
    if (!surveyAnswer && !!userIdentifier && !!survey) {
      initAnswer()
    }
  }, [surveyAnswer, userIdentifier, survey, checkForExistingLeaderAnswer, checkForExistingTeamMemberAnswer, surveyTimeLimitReached])

  const submit = async (): Promise<boolean> => {
    window.scrollTo(0, 0)
    if (previewMode) {
      setStatus(Status.SUCCESS)
      return true
    } else {
      const result = await saveAnswerSet(surveyAnswer)
      if (!!result) {
        setStatus(Status.SUCCESS)
      } else {
        toast(t.survey.answer.submit.error)
      }
      return !!result
    }
  }

  const updateElement = (uuid: string) => (answer: Element) => {
    const updatedAnswers = surveyAnswer.elements.map((a) => (a.uuid === uuid ? answer : a))
    setSurveyAnswer({ ...surveyAnswer, elements: updatedAnswers })
  }

  const updateOrganizationUnitUUID = async (organizationUnitUUID: string) => {
    // check for existing manager answer
    if (survey.surveyType === SurveyType.MANAGER) {
      const answer = await checkForExistingManagerAnswer(survey, organizationUnitUUID)

      if (!!answer) {
        setSurveyAnswer(answer)
        setPrefilledType(PrefilledType.MANAGER)
        return
      } else {
        setPrefilledType(PrefilledType.NONE)
      }
      // if its a prefilled team survey just change the organization unit
    } else if (PrefilledType.TEAM === prefilledType && survey.surveyType === SurveyType.TEAM) {
      setSurveyAnswer({ ...surveyAnswer, organizationUnitUUID })
      return
    }
    // initialize answer with organizationUnitUUID
    let elements = transformElementsToAnswers(survey.elements)
    setSurveyAnswer({
      surveyUUID: survey.uuid,
      userIdentifier: userIdentifier,
      organizationUnitUUID,
      elements,
    })
  }

  const updateDayNumber = (dayNumber: number) => {
    setSurveyAnswer({ ...surveyAnswer, dayNumber })
  }

  return !!surveyAnswer
    ? {
        survey,
        surveyAnswer,
        updateElement,
        updateOrganizationUnitUUID,
        updateDayNumber,
        submit,
        prefilledType,
        status,
        timeLimitReached: surveyTimeLimitReached(),
      }
    : {
        prefilledType,
        status,
        timeLimitReached: surveyTimeLimitReached(),
      }
}

export default useSurveyAnswer
